import { MouseEvent, useMemo } from 'react';

import { GridColDef } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';

import DashboardDataGrid from 'src/common/components/dataGrid/DashboardDataGrid';
import { handymanActionsColumn } from 'src/common/components/dataGrid/dashboardGridColumns';
import { LanguageKeys } from 'src/common/constants/languages';
import { ITrackMap } from 'src/interfaces/TrackMap';
import useSystemOptionsStore from 'src/stores/systemOptionsStore';

import {
  TrackMapRowActionsMenu,
  formatTrackMapLevelDiff,
  formatTrackMapTime,
  formatTrackMapTotalDistance,
  getTrackMapDistanceName,
} from './trackMapTableCells';

interface Props {
  trackMaps: ITrackMap[];
  onDeleteTrackMap: (trackMapId: string, e?: MouseEvent<unknown>) => void;
  onEditTrackMap: (trackMapId: string, e?: MouseEvent<unknown>) => void;
}

const TrackMapsTable = ({ onDeleteTrackMap, onEditTrackMap, trackMaps }: Props) => {
  const {
    t,
    i18n: { resolvedLanguage },
  } = useTranslation();
  const currentLang = resolvedLanguage as LanguageKeys;
  const { distances, distancesById } = useSystemOptionsStore(systemOptionsState => systemOptionsState);

  const columns = useMemo(
    (): GridColDef<ITrackMap>[] => [
      {
        field: 'name',
        headerName: t('system.trackMaps.nameLabel'),
        width: 140,
        sortable: false,
      },
      {
        field: 'distance',
        headerName: t('system.trackMaps.distance'),
        width: 110,
        sortable: false,
        valueGetter: (_, row) =>
          getTrackMapDistanceName(row.distance, currentLang, distances, distancesById),
      },
      {
        field: 'route',
        headerName: t('system.trackMaps.routeLabel'),
        flex: 2,
        minWidth: 220,
        sortable: false,
        valueGetter: (_, row) => row.route.i18n[currentLang],
      },
      {
        field: 'totalDistance',
        headerName: t('system.trackMaps.routeLengthLabel'),
        width: 130,
        sortable: false,
        valueGetter: (_, row) => formatTrackMapTotalDistance(row.totalDistance, t),
      },
      {
        field: 'levelDiff',
        headerName: t('system.trackMaps.levelLabel'),
        width: 140,
        sortable: false,
        valueGetter: (_, row) => formatTrackMapLevelDiff(row.levelDiff, t),
      },
      {
        field: 'time',
        headerName: t('system.trackMaps.timeLabel'),
        width: 110,
        sortable: false,
        valueGetter: (_, row) => formatTrackMapTime(row.time, t),
      },
      {
        field: 'published',
        headerName: t('system.sponsors.published'),
        width: 100,
        sortable: false,
        valueGetter: (_, row) => (row.published ? '✓' : '-'),
      },
      handymanActionsColumn<ITrackMap>(t, 'system.trackMaps.optionsLabel', ({ row }) => (
        <TrackMapRowActionsMenu
          trackMapId={row.id}
          onEdit={e => onEditTrackMap(row.id, e)}
          onDelete={e => onDeleteTrackMap(row.id, e)}
        />
      )),
    ],
    [currentLang, distances, distancesById, onDeleteTrackMap, onEditTrackMap, t],
  );

  return (
    <DashboardDataGrid<ITrackMap>
      aria-label={t('system.trackMaps.trackMapsTableLabel')}
      chrome="compact"
      rows={trackMaps}
      columns={columns}
    />
  );
};

export default TrackMapsTable;
